/**
 * DispatchLeftRail — Feb 2026 (Slice 2 · Step 6, final)
 *
 * Persistent left-rail primary nav for dispatchers / super-admins on
 * DESKTOP viewports (>=lg / 1024px). Mirrors the mobile `BottomTabBar`:
 * same 5 domains, same accents, same live-polled badges.
 *
 * Rendered exactly once at the app shell alongside BottomTabBar; each
 * component uses Tailwind visibility classes so the two never appear
 * simultaneously.
 *
 * Body-margin safeguard: while the rail is mounted we toggle a
 * `has-left-rail` class on <body> so page layouts can pad their left edge
 * (the class is defined in App.css).
 */
import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Radio, TrendingUp, Star, Users, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'ops',     label: 'OPS',     to: '/dispatch',                    icon: Radio,      accent: '#00F0FF' },
  { key: 'growth',  label: 'GROWTH',  to: '/dispatch/admin/launch-stats', icon: TrendingUp, accent: '#FF9F0A' },
  { key: 'reviews', label: 'REVIEWS', to: '/dispatch/admin/reviews',      icon: Star,       accent: '#FFD60A' },
  { key: 'people',  label: 'PEOPLE',  to: '/dispatch/admin/users',        icon: Users,      accent: '#39FF14' },
  { key: 'system',  label: 'SYSTEM',  to: '/dispatch/settings',           icon: Settings,   accent: '#B87BFF' },
];

// Same resolver as BottomTabBar so the two stay perfectly in sync.
function activeTabKey(pathname) {
  if (pathname.startsWith('/dispatch/admin/reviews')) return 'reviews';
  if (pathname.startsWith('/dispatch/admin/launch-stats')) return 'growth';
  if (pathname.startsWith('/dispatch/admin/leaderboards')) return 'growth';
  if (pathname.startsWith('/dispatch/admin/promo-agent')) return 'growth';
  if (pathname.startsWith('/dispatch/admin/boost')) return 'growth';
  if (pathname.startsWith('/dispatch/admin/users')) return 'people';
  if (pathname.startsWith('/dispatch/admin/points')) return 'people';
  if (pathname.startsWith('/dispatch/settings')) return 'system';
  if (pathname.startsWith('/dispatch/admin/edit-profile')) return 'system';
  if (pathname.startsWith('/dispatch/admin/login-stats')) return 'system';
  if (pathname.startsWith('/dispatch/admin/push-health')) return 'system';
  if (pathname.startsWith('/dispatch/admin/email-health')) return 'system';
  if (pathname.startsWith('/dispatch')) return 'ops';
  return null;
}

export function DispatchLeftRail() {
  const { user } = useAuth();
  const { pathname } = useLocation();

  const isDispatchScope = pathname.startsWith('/dispatch');
  const isAuthorized = user && (user.role === 'dispatcher' || user.role === 'super_admin');
  const shouldShow = isAuthorized && isDispatchScope;

  const [badges, setBadges] = useState({ ops: 0, reviews: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!shouldShow) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const [chatRes, revRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/chat`, { withCredentials: true })
            .catch(() => ({ data: { unread_total: 0 } })),
          axios.get(`${API_URL}/api/admin/reviews/pending-count`, { withCredentials: true })
            .catch(() => ({ data: { count: 0 } })),
        ]);
        if (cancelled) return;
        setBadges({
          ops: Number(chatRes.data?.unread_total || 0),
          reviews: Number(revRes.data?.count || 0),
        });
      } catch {
        // silent — badges default to 0 on any error
      }
    };
    load();
    timerRef.current = setInterval(load, 30000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [shouldShow]);

  // Pad page bodies on the left while the rail is showing (lg+ only —
  // enforced via CSS media query in App.css).
  useEffect(() => {
    if (!shouldShow) return;
    document.body.classList.add('has-left-rail');
    return () => document.body.classList.remove('has-left-rail');
  }, [shouldShow]);

  if (!shouldShow) return null;

  const active = activeTabKey(pathname);

  return (
    <aside
      className="hidden lg:flex fixed left-0 top-16 bottom-0 z-40 w-20 flex-col bg-[#0a0510]/95 backdrop-blur border-r border-[#2A0F3D]"
      data-testid="dispatch-left-rail"
      aria-label="Primary navigation"
    >
      <ul className="flex flex-col items-stretch py-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = active === tab.key;
          const badge = badges[tab.key] || 0;
          return (
            <li key={tab.key}>
              <Link
                to={tab.to}
                data-testid={`left-rail-${tab.key}`}
                className="relative flex flex-col items-center justify-center gap-1 py-3 px-1 font-mono text-[9px] uppercase tracking-widest transition-colors border-l-2"
                style={{
                  color: isActive ? tab.accent : '#4B6B52',
                  borderLeftColor: isActive ? tab.accent : 'transparent',
                  backgroundColor: isActive ? 'rgba(255,255,255,0.02)' : 'transparent',
                }}
                title={tab.label}
              >
                <span className="relative inline-flex">
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2.25 : 1.75} />
                  {badge > 0 && (
                    <span
                      data-testid={`left-rail-${tab.key}-badge`}
                      className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center leading-none"
                      style={{ backgroundColor: '#FF3B30', color: '#0a0510' }}
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </span>
                <span>{tab.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

export default DispatchLeftRail;
